import { useEffect, useState } from 'react'
import styled from 'styled-components'

import AddressDisplay from 'checkout/components/AddressDisplay'
import RadioButton from 'checkout/components/RadioButton'
import RadioGroup from 'checkout/components/RadioGroup'
import Spinner from 'shared/components/Spinner'
import { Button } from 'shared/components/Button'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors } from 'shared/lib'
import { t } from 'localization'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useGetAddressesQuery } from 'api/addressesApi'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'

const TP = 'checkout.panels.ShippingAddress.SavedShippingAddressForm'

export const SavedShippingAddressForm = () => {
  const {
    connectAddressToOrderRTK,
    editPanel,
    order,
    panels: { shippingAddress },
    updatePanel,
    userType,
  } = useCheckoutContext()
  const isGuestCheckout = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const isExposedPaymentMethods = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EXPOSED_PAYMENT_METHODS)

  const { country: selectedCountryCode } = useShoppingRegionContext()
  const { data, isLoading } = useGetAddressesQuery('shipping')

  const isFormLoading = shippingAddress.loading || isLoading
  const shippingAddressId = order?.addressId
  const addresses = data?.addresses || []
  const [selectedAddressId, setSelectedAddressId] = useState(shippingAddressId || addresses[0]?.id)

  const [suggestAddressEdit, setSuggestAddressEdit] = useState<boolean>(false)
  const isAnyShippingAddressInRegion = !!(
    addresses && addresses.find((a) => a.countryCode === selectedCountryCode)
  )

  if (shippingAddress.view === 'saved' && !isAnyShippingAddressInRegion && !suggestAddressEdit) {
    editPanel('shippingAddress')
    setSuggestAddressEdit(true)
  }

  const handleClickAddNewAddress = () => {
    const view = (() => {
      if (isGuestCheckout) {
        return userType === 'guest' ? 'guest' : 'new'
      }
      return 'new'
    })()
    updatePanel('shippingAddress', { view })
  }

  useEffect(() => {
    if (shippingAddressId) {
      setSelectedAddressId(shippingAddressId)
    }
  }, [shippingAddressId])

  return (
    <Spinner
      showSpinner={isFormLoading}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
      text={t(`${TP}.spinnerText`, 'Saving address...')}
    >
      <AddressRadioGroup qaAttr={CheckoutQA.ShippingAddressRadioGroup} name="shippingAddress">
        {addresses.map((address) => (
          <RadioButton
            checked={selectedAddressId === address.id}
            displayRadioButton={isExposedPaymentMethods}
            key={address.id}
            marginBetween="10px 0"
            onRadioSelect={() => setSelectedAddressId(address.id)}
            qaAttr={CheckoutQA.ShippingAddress}
            renderTitle={
              <AddressLine
                name={address.name}
                address1={address.address1}
                address2={address.address2}
                city={address.city}
                state={address.state}
                postalCode={address.postalCode}
              />
            }
            value={`${address.id}`}
          />
        ))}
      </AddressRadioGroup>
      <ContinueButton
        $fill
        buttonType="primary1"
        onClick={() => selectedAddressId && connectAddressToOrderRTK(selectedAddressId)}
        qaAttr={CheckoutQA.ShippingContiuneButton}
        type="submit"
      >
        {t(`${TP}.continue`, 'Continue')}
      </ContinueButton>
      <Button
        $fill
        buttonType="secondary"
        onClick={handleClickAddNewAddress}
        qaAttr={CheckoutQA.ShippingAddNewAddressButton}
      >
        {t(`${TP}.addNewAddress`, 'Add New Address')}
      </Button>
    </Spinner>
  )
}

const AddressLine = styled(AddressDisplay)`
  color: ${colors.FC2_BLACK};
  line-height: 18px;
`
const AddressRadioGroup = styled(RadioGroup)`
  margin-bottom: 20px;
`
const ContinueButton = styled(Button)`
  margin: 5px 0 15px;
`
